// The flight's path, timed by the server's times as Lagos Life's flights are: boarding at the gate, then the
// take-off roll (turning off the apron onto the runway), lift-off, the climb through the clouds, the cruise,
// the descent, touchdown and the roll-out to a stop as the jet lands on time. Distances in metres along the
// way (x), height above the ground (y); the gate stands off the runway (z).

export const CRUISE_HEIGHT = 260;
// the gate, beside the start of the runway
export const GATE = { x: -260, z: -48 };
// the runway (along x, centred on z = 0), from the start of the roll
export const RUNWAY_LENGTH = 1500;
const ROLL_SECONDS = 7;
const LIFT_OFF = 6;
const ROLL_SPEED = 75;
const CRUISE_SPEED = 200;
const APPROACH_SPEED = 70;
const DESCENT_SECONDS = 9;
const ROLL_OUT_SECONDS = 3;
const STEP = 0.05;

const smooth = (t: number) => {
  const k = Math.max(0, Math.min(1, t));
  return k * k * (3 - 2 * k);
};

export type FlightPoint = {
  x: number;
  y: number;
  z: number;
  // the nose up (radians) and which way it points across (radians from +x)
  pitch: number;
  heading: number;
  gearDown: boolean;
  // how far through: 0 at take-off, 1 stopped at the other airport
  progress: number;
};

export function flightProfile(seconds: number) {
  const total = Math.max(ROLL_SECONDS + DESCENT_SECONDS + ROLL_OUT_SECONDS + 4, seconds);
  const touchdown = total - ROLL_OUT_SECONDS;
  const descentStarts = touchdown - DESCENT_SECONDS;
  const climbEnds = Math.max(LIFT_OFF + 6, Math.min(descentStarts - 1, total * 0.5));
  const speedAt = (t: number) => {
    if (t <= 0) return 0;
    if (t < ROLL_SECONDS) return (ROLL_SPEED * t) / ROLL_SECONDS;
    if (t < climbEnds)
      return ROLL_SPEED + (CRUISE_SPEED - ROLL_SPEED) * smooth((t - ROLL_SECONDS) / (climbEnds - ROLL_SECONDS));
    if (t < descentStarts) return CRUISE_SPEED;
    if (t < touchdown)
      return CRUISE_SPEED + (APPROACH_SPEED - CRUISE_SPEED) * smooth((t - descentStarts) / DESCENT_SECONDS);
    if (t < total) return APPROACH_SPEED * (1 - (t - touchdown) / ROLL_OUT_SECONDS);
    return 0;
  };
  // the distance gone, added up in small steps once
  const distances: number[] = [0];
  for (let t = STEP; t <= total + STEP; t += STEP)
    distances.push(distances[distances.length - 1]! + ((speedAt(t - STEP) + speedAt(t)) / 2) * STEP);
  const distanceAt = (t: number) => {
    if (t <= 0) return 0;
    const i = Math.min(distances.length - 2, Math.floor(t / STEP));
    const k = Math.min(1, (t - i * STEP) / STEP);
    return distances[i]! + (distances[i + 1]! - distances[i]!) * k;
  };
  const heightAt = (t: number) => {
    if (t < LIFT_OFF || t >= touchdown) return 0;
    if (t < descentStarts) return CRUISE_HEIGHT * smooth((t - LIFT_OFF) / (climbEnds - LIFT_OFF));
    return CRUISE_HEIGHT * (1 - smooth((t - descentStarts) / DESCENT_SECONDS));
  };
  // off the apron onto the runway over the first stretch of the roll
  const across = (gone: number) => GATE.z * (1 - smooth(gone / 220));
  const start = GATE.x;
  const pointAt = (t: number): FlightPoint => {
    const gone = distanceAt(t);
    const ahead = distanceAt(t + 0.1);
    const x = start + gone;
    const z = across(gone);
    const y = heightAt(t);
    const dx = Math.max(0.001, ahead - gone);
    const dy = heightAt(t + 0.1) - y;
    const dz = across(ahead) - z;
    // nose up for the climb (a little more than the path), level in the cruise, a gentle flare to land
    const climbing = Math.atan2(dy, dx);
    const flare = t > touchdown - 2.2 && t < touchdown ? 0.07 : 0;
    const rotate = t > LIFT_OFF - 1.2 && t < LIFT_OFF + 2 ? 0.14 * smooth((t - (LIFT_OFF - 1.2)) / 1.2) : 0;
    return {
      x,
      y,
      z,
      pitch: Math.max(climbing + 0.03 + flare, rotate, 0),
      heading: Math.atan2(-dz, dx),
      gearDown: t < LIFT_OFF + 2.5 || t > touchdown - 7,
      progress: Math.max(0, Math.min(1, t / total)),
    };
  };
  return {
    total,
    touchdown,
    // where the jet touches down (the other airport's runway is laid there)
    landsAt: start + distanceAt(touchdown),
    stopsAt: start + distanceAt(total),
    pointAt,
  };
}
